import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
// eslint-disable-next-line import/named
import { TranslateFunction } from 'react-localize-redux';

import { ErrorBoundary } from '../../../App/ErrorBoundary';
import { Button, Typography } from '../../../components/atoms';

const useStyles = makeStyles((theme: Theme) => ({
  cancelButton: {
    backgroundColor: theme.palette.common.white,
    border: 'none',
    padding: theme.spacing(1, 0),
    width: '160px',

    '&.fd-button div': {
      color: theme.palette.success.main,
    },

    '&:disabled': {
      opacity: 0.4,
    },

    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(1.5),
    },
  },
  orderCompleteAlert: {
    alignItems: 'flex-start',
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    display: 'flex',
    flexFlow: 'column nowrap',
    marginBottom: theme.spacing(2.5),
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),

    '& .fd-text': {
      color: theme.palette.common.white,
    },

    [theme.breakpoints.up('sm')]: {
      alignItems: 'center',
      flexFlow: 'row nowrap',
      justifyContent: 'space-between',
      padding: theme.spacing(3),
    },

    [theme.breakpoints.up('md')]: {
      marginTop: 0,
    },
  },

  orderCompleteCancel: {
    backgroundColor: theme.palette.info.main,
  },
}));

type Props = {
  cancelTimeout?: number;
  countDown: number;
  handleCancelOrder: () => void;
  isCancelled: boolean;
  translate: TranslateFunction;
};

export function OrderStatusBar(props: Props): JSX.Element {
  const {
    cancelTimeout,
    countDown,
    handleCancelOrder,
    isCancelled,
    translate,
  } = props;

  const classes = useStyles();

  const countingFinished = !countDown;

  const orderCompleteStyles = clsx(
    classes.orderCompleteAlert,
    isCancelled && classes.orderCompleteCancel,
  );

  const orderStatusTitle = isCancelled
    ? translate('Checkout_orderHasBeenCancelled')
    : translate('Order_has_been_placed');

  return (
    <ErrorBoundary identifier="order-status-bar">
      <div className={orderCompleteStyles}>
        <div>
          <Typography variant="h2">{orderStatusTitle}</Typography>

          {!isCancelled && (
            <Typography variant="body2">
              {translate('We_will_send_you_an_update')}
            </Typography>
          )}
        </div>

        {cancelTimeout > 0 && (
          <Button
            aria-label={`${translate('Cancel')} ${countDown}`}
            className={classes.cancelButton}
            data-fd="order-cancel-button"
            disabled={countingFinished}
            onClick={() => {
              handleCancelOrder();
            }}
            variant="secondary"
          >
            {`${translate('Cancel')} ${countDown}`}
          </Button>
        )}
      </div>
    </ErrorBoundary>
  );
}
